import React, {useContext} from 'react';
import {
  func, string, bool, shape, oneOf, arrayOf, oneOfType
} from "prop-types";
import { extend } from "@thd-nucleus/data-sources";
import { Drawer, DrawerHeader } from "@one-thd/sui-atomic-components";
import { AddToSelection } from "./common/addto-selection/AddToSelection";
import { AddToProject } from "./addto/project/AddToProject";
import { AddToList } from "./addto/list/AddToList";
import { CopyProject } from "./addto/copyProject/CopyProject";
import { LoadingStatus } from "./common/products-loading-status/LoadingStatus";
import { ProductExceptions } from "./common/product-exceptions/ProductExceptions";
import { AddToContext } from "./AddToContext";
import { ADD_TO_TYPE, SCREENS } from "./AddToConstants";
import { handleOpenATQ } from './AddToHelpers';


export const AddToDrawer = ({
                              open = false,
                              setAddToType,
                              updateLoadedProductsData,
                              showBackButton,
                              loadedProductsData,
                              projectDetails,
                              loadingProgress,
                              list,
                              onSuccessfulAddToGroup,
                              initialLoad,
                              setInitialLoad,
                              isProjectsEligible,
                              copyProjectData,
                              nonQuoteableProducts,
                              selectedCatalogItems,
                              selectedQuotableCatalogItemDetails,
                              addToQuoteRef
                            }) => {

  const {
    screen, addToType, handleClose, setAbandonWarning, canShowWarning, products
  } = useContext(AddToContext);

  const onDrawerClose = (__event) => {
    if (canShowWarning) {
      setAbandonWarning({ show: true, action: 'CLOSE' });
      return;
    }
    handleClose(__event);
  }

  const { ADD_TO_SELECTION, LOADING_STATUS, PRODUCT_EXCEPTIONS } = SCREENS;

  return (
    <Drawer open={open} onClose={onDrawerClose}>
      <>
        {screen === ADD_TO_SELECTION && (
          <AddToSelection
            products={products}
            setAddToType={setAddToType}
            handleClose={handleClose}
            isProjectsEligible={isProjectsEligible}
            copyProjectData={copyProjectData?.items}
          />
        )}
        {
          (addToType === 'Project') && (
            <AddToProject
              products={products}
              projectId={projectDetails?.id}
              initialLoad={initialLoad}
              setInitialLoad={setInitialLoad}
              onSuccessfulAddToGroup={onSuccessfulAddToGroup}
              showBackButton={showBackButton}
            />
          )
        }
        {
          (addToType === 'List') && (
            <AddToList
              products={selectedCatalogItems ?? products}
              showCount={list?.showCount}
              skipListId={list?.skipListId}
              isSharedList={list?.isSharedList}
              listId={list?.listId}
              onMaterialsAdded={list?.onMaterialsAdded}
            />
          )
        }
        {
          (addToType === 'CopyProject') && (
            <CopyProject
              copyProjectData={copyProjectData}
            />
          )
        }
        {
          (addToType === ADD_TO_TYPE.QUOTE) && (
            <>
              <DrawerHeader
                title={`Add to ${addToType}`}
                onClose={handleClose}
              />
              {screen === LOADING_STATUS && (
                <LoadingStatus
                  products={products}
                  loadingProgress={loadingProgress}
                  projectDetails={projectDetails}
                  loadedProductsData={loadedProductsData}
                  updateLoadedProductsData={updateLoadedProductsData}
                />
              )}
              {screen === PRODUCT_EXCEPTIONS && (
                <ProductExceptions
                  productExceptions={nonQuoteableProducts}
                  handleOpenATQ={() => handleOpenATQ(addToQuoteRef)}
                  showATQ={selectedQuotableCatalogItemDetails?.length > 0}
                />
              )}
            </>
          )
        }
      </>
    </Drawer>
  )
}

AddToDrawer.propTypes = {
  addToType: oneOf(
    [ADD_TO_TYPE.PROJECT, ADD_TO_TYPE.LIST, ADD_TO_TYPE.QUOTE, ADD_TO_TYPE.CART, ADD_TO_TYPE.COPY_PROJECT, null]
  ),
  list: shape({
    showCount: bool,
    isSharedList: bool,
    showIconButton: bool,
    skipListId: string,
    listId: string,
    onMaterialsAdded: func,
    isMaterialsButton: bool
  }),
  setAddToType: func.isRequired,
  showBackButton: bool.isRequired,
  open: bool.isRequired,
  isProjectsEligible: bool.isRequired,
  setInitialLoad: func.isRequired,
  initialLoad: bool.isRequired,
  loadedProductsData: arrayOf(shape({})),
  nonQuoteableProducts: arrayOf(shape({})),
  selectedQuotableCatalogItemDetails: arrayOf(shape({})),
  selectedCatalogItems: arrayOf(shape({})),
  projectDetails: shape({
    id: string,
    projectId: string,
    projectName: string,
    storeId: string,
    zipCode: string
  }),
  copyProjectData: shape({
    sourceList: string,
    items: arrayOf(
      oneOfType([string, shape({})])
    ),
    targetProjectId: string
  }),
  onSuccessfulAddToGroup: func.isRequired,
  updateLoadedProductsData: func.isRequired
};

AddToDrawer.defaultProps = {
  loadedProductsData: [],
  projectDetails: {},
  copyProjectData: {},
  nonQuoteableProducts: [],
  selectedCatalogItems: null
};

AddToDrawer.displayName = "AddToDrawer";

AddToDrawer.dataModel = extend(
  AddToProject,
  AddToList,
  LoadingStatus,
  CopyProject
);
